import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

import type { Company, Contact } from "../types";
import type { AgentActivityRun } from "./useAgentActivity";

// Same status-color mapping as AgentActivityList.tsx's desktop table — kept
// as its own copy rather than a shared export, same reasoning as every other
// small option/label list duplicated per-file this session (e.g.
// LEAD_SOURCE_OPTIONS in ContactListFilter/LeadSourceSelector/
// BulkLeadSourceButton): each surface's copy can diverge later without
// cross-breaking the other.
const STATUS_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  completed: "default",
  success: "default",
  running: "secondary",
  pending: "secondary",
  failed: "destructive",
  failure: "destructive",
  cancelled: "outline",
  partial: "outline",
};

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const ActivityTarget = ({
  run,
  contacts,
  companies,
}: {
  run: AgentActivityRun;
  contacts?: Contact[];
  companies?: Company[];
}) => {
  if (run.target_contact_id) {
    const contact = contacts?.find(
      (c) => Number(c.id) === run.target_contact_id,
    );
    return (
      <Link to={`/contacts/${run.target_contact_id}/show`} className="hover:underline">
        {contact ? `${contact.first_name} ${contact.last_name}` : `Contact #${run.target_contact_id}`}
      </Link>
    );
  }
  if (run.target_company_id) {
    const company = companies?.find(
      (c) => Number(c.id) === run.target_company_id,
    );
    return (
      <Link to={`/companies/${run.target_company_id}/show`} className="hover:underline">
        {company?.name ?? `Company #${run.target_company_id}`}
      </Link>
    );
  }
  return null;
};

/**
 * Mobile card for one agent_runs row — same visual language as
 * ApprovalQueueCard.tsx (badge row, primary line, muted detail line,
 * timestamp), read-only (agent activity has no approve/reject action, unlike
 * the approval queue).
 */
export const AgentActivityCard = ({
  run,
  contacts,
  companies,
}: {
  run: AgentActivityRun;
  contacts?: Contact[];
  companies?: Company[];
}) => {
  return (
    <Card>
      <CardContent className="flex flex-col gap-2 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{run.agent_name}</Badge>
          <Badge variant={STATUS_VARIANT[run.status] ?? "outline"}>
            {run.status}
          </Badge>
          <span className="text-muted-foreground ml-auto text-xs">
            {fmtDateTime(run.created_at)}
          </span>
        </div>
        <p className="text-sm font-medium">
          {run.run_type}
          {run.trigger_type && (
            <span className="text-muted-foreground ml-1 text-xs font-normal">
              via {run.trigger_type}
            </span>
          )}
        </p>
        <div className="text-muted-foreground text-xs">
          <ActivityTarget run={run} contacts={contacts} companies={companies} />
        </div>
      </CardContent>
    </Card>
  );
};
